// ── bulk-import-modal: the file → preview → confirm → result flow (@vjal/ui) ──
// Wave 5 (Bulk participant import). The whole import lives in ONE Modal with a
// small internal step machine; the OWNING PAGE supplies the mutation (so the
// idempotency-key + cache invalidation stay in the existing hook) and closes +
// toasts only after the backend confirms.
//
// Honesty rules enforced here (blueprint §6 gate #8):
//   • CLIENT-SIDE preview only validates (no server preview exists — see
//     bulk-import-parse.ts). Bad type / empty / missing column / bad email /
//     duplicate each render an explicit state; Confirm is DISABLED until there is
//     at least one ready row and ZERO invalid/duplicate rows.
//   • The Confirm POST renders the AUTHORITATIVE backend result. On a server
//     IMPORT_VALIDATION_FAILED (CO_VALIDATION_FAILED) or CO_CUTOFF_EXCEEDED
//     refusal, the catch shows the honest row/cutoff message — NO success toast
//     (the page only toasts on a committed success; this modal stays open).
//   • A closed coach cutoff (state="closed") renders a gate-closed posture and
//     blocks the file picker entirely (the page also hides the trigger).

import { Alert, Badge, Button, Card, CollectionPagination, DataTableV2, Modal } from "@vjal/ui";
import { useRef, useState } from "react";

import { HonestState } from "../../../components/honest-state";
import type { CoachingOperationResult } from "../../../services/coaching-dtos";
import { describeParticipantWriteError } from "../participants-helpers";
import {
  parseBulkImportText,
  readFileAsText,
  readyRowsToRequest,
  type BulkImportParseResult,
} from "./bulk-import-parse";
import { describeBulkImportResult, type BulkImportResultView } from "./bulk-import-result";
import {
  useBulkImportPreviewCollection,
  useBulkImportResultCollection,
  type PreviewTableRow,
  type ResultTableRow,
} from "./bulk-import-collection";
import { isCutoffExceededError, type CutoffDecision } from "./coach-cutoff";

export interface BulkImportModalProps {
  open: boolean;
  cohortLabel: string;
  /** Emails already active in the target cohort (lowercased) — flags duplicates. */
  existingEmails?: Iterable<string>;
  /** The coach Week-3 cutoff decision for the acting role + cohort. */
  cutoff: CutoffDecision;
  onClose: () => void;
  /** The actual write — owned by the page (the bulkImport react-query mutation). */
  onSubmit: (rows: Array<Record<string, unknown>>) => Promise<CoachingOperationResult>;
  submitting?: boolean;
}

type Step = "select" | "preview" | "result";

export function BulkImportModal({
  open,
  cohortLabel,
  existingEmails,
  cutoff,
  onClose,
  onSubmit,
  submitting = false,
}: BulkImportModalProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [step, setStep] = useState<Step>("select");
  const [fileName, setFileName] = useState<string>("");
  const [parsed, setParsed] = useState<BulkImportParseResult | null>(null);
  const [result, setResult] = useState<BulkImportResultView | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);


  function reset() {
    setStep("select");
    setFileName("");
    setParsed(null);
    setResult(null);
    setSubmitError(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleFile(file: File | undefined) {
    setSubmitError(null);
    setResult(null);
    if (!file) return;
    setFileName(file.name);
    let text: string;
    try {
      text = await readFileAsText(file);
    } catch {
      setParsed({
        ok: false,
        fileError: "Couldn't read that file. Try a CSV exported from your spreadsheet.",
        delimiter: null,
        headers: [],
        rows: [],
        readyCount: 0,
        invalidCount: 0,
        duplicateCount: 0,
      });
      setStep("preview");
      return;
    }
    setParsed(parseBulkImportText(text, { fileName: file.name, existingEmails }));
    setStep("preview");
  }

  async function handleConfirm() {
    if (!parsed || !parsed.ok) return;
    const body = readyRowsToRequest(parsed.rows);
    if (body.length === 0) return;
    setSubmitError(null);
    try {
      const operation = await onSubmit(body);
      const view = describeBulkImportResult(operation, body.length);
      setResult(view);
      setStep("result");
      // The PAGE toasts + invalidates on a committed success; a server-reported
      // validation/partial failure stays here as an honest result (no toast).
    } catch (error) {
      // IMPORT_VALIDATION_FAILED / cutoff / any thrown error: show it honestly,
      // stay on the preview step, NO success toast.
      setSubmitError(error);
    }
  }

  const cutoffClosed = cutoff.state === "closed";
  const topError = describeParticipantWriteError(submitError);
  const submitErrorIsCutoff = isCutoffExceededError(submitError);
  const canConfirm = Boolean(parsed?.ok) && (parsed?.readyCount ?? 0) > 0 && !cutoffClosed;
  // A 410 CO_CUTOFF_EXCEEDED is authoritative even when the client's cutoff view is stale or
  // unknown: show the server's message (it states the cutoff was exceeded), not the local reason.
  function serverCutoffMessage(error: unknown, fallback: string): string {
    const message = error instanceof Error && error.message.trim() ? error.message.trim() : fallback;
    return /no change was made/i.test(message) ? message : `${message} No change was made.`;
  }

  return (
    <Modal open={open} onClose={handleClose} title={`Bulk import participants — ${cohortLabel}`}>
      <div className="flex flex-col gap-4" data-testid="bulk-import-modal">
        {/* The coach Week-3 cutoff (closed) hard-gates the whole flow. */}
        {cutoffClosed ? (
          <HonestState
            posture="gate-closed"
            gate={{ closed: true, reason: cutoff.reason }}
            title="The add window for this cohort has closed"
          />
        ) : (
          <>
            {cutoff.state === "unknown" && (
              <Alert variant="info">{cutoff.reason}</Alert>
            )}

            {step === "select" && <SelectStep inputRef={inputRef} fileName={fileName} />}

            {step === "preview" && parsed && (
              <PreviewStep
                parsed={parsed}
                topError={submitErrorIsCutoff ? null : topError?.message ?? null}
                cutoffError={submitErrorIsCutoff ? serverCutoffMessage(submitError, cutoff.reason) : null}
                onReplaceFile={() => inputRef.current?.click()}
              />
            )}

            {step === "result" && result && (
              <ResultStep result={result} />
            )}

            {/* The hidden picker is always mounted so "Choose another file" works. */}
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.tsv,text/csv,text/tab-separated-values,text/plain"
              className="sr-only"
              aria-label="Participant CSV file"
              onChange={(event) => void handleFile(event.target.files?.[0])}
            />

            <div className="mt-2 flex items-center justify-end gap-3">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
                {step === "result" ? "Close" : "Cancel"}
              </Button>
              {/* The Import button shows only on a parseable preview (a whole-file
                  error has no importable rows → only "Choose another file"). */}
              {step === "preview" && parsed && !parsed.fileError && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => void handleConfirm()}
                  isLoading={submitting}
                  disabled={!canConfirm || submitting}
                >
                  Import {parsed.readyCount} participant{parsed.readyCount === 1 ? "" : "s"}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function SelectStep({
  inputRef,
  fileName,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  fileName: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-sm text-ink-muted">
        Upload a CSV with an <code>email</code> column (optional: <code>name</code>, <code>first_name</code>,{" "}
        <code>last_name</code>, <code>phone</code>). We validate every row before anything is imported.
      </p>
      <div>
        <Button type="button" variant="secondary" onClick={() => inputRef.current?.click()}>
          Choose CSV file
        </Button>
        {fileName ? <span className="ml-3 text-sm text-ink-muted">{fileName}</span> : null}
      </div>
      <Alert variant="info">
        Up to 250 participants per import. Excel <code>.xlsx</code> isn&apos;t supported — export the sheet as CSV first.
      </Alert>
    </div>
  );
}

function PreviewStep({
  parsed,
  topError,
  cutoffError,
  onReplaceFile,
}: {
  parsed: BulkImportParseResult;
  topError: string | null;
  cutoffError: string | null;
  onReplaceFile: () => void;
}) {
  const previewRows = parsed.rows as PreviewTableRow[];
  const collection = useBulkImportPreviewCollection(previewRows);
  if (parsed.fileError) {
    return (
      <div className="flex flex-col gap-3" data-testid="bulk-import-file-error">
        <Alert variant="error">{parsed.fileError}</Alert>
        <div>
          <Button type="button" variant="secondary" onClick={onReplaceFile}>
            Choose another file
          </Button>
        </div>
      </div>
    );
  }

  const hasProblems = parsed.invalidCount > 0 || parsed.duplicateCount > 0;

  return (
    <div className="flex flex-col gap-3">
      {cutoffError && (
        <Alert variant="warning" data-testid="bulk-import-cutoff-error">
          {cutoffError} No change was made.
        </Alert>
      )}
      {topError && <Alert variant="error">{topError}</Alert>}

      <div
        className="grid grid-cols-3 gap-3"
        data-testid="bulk-import-summary"
        role="group"
        aria-label="Import validation summary"
      >
        <SummaryBadge label="Ready" value={parsed.readyCount} variant="success" />
        <SummaryBadge label="Duplicate" value={parsed.duplicateCount} variant="warning" />
        <SummaryBadge label="Invalid" value={parsed.invalidCount} variant="danger" />
      </div>

      {hasProblems && (
        <Alert variant="warning">
          Fix or remove the highlighted rows in your CSV and upload again. Import stays disabled until every row is ready.
        </Alert>
      )}

      <div className="rounded-xl border border-line bg-white">
        <DataTableV2
          collection={collection}
          caption="Bulk import preview — no participants are changed until you confirm"
          emptyTitle="No rows to import"
          emptyMessage="The file had a header but no participant rows."
        />
        {collection.uiState === "ready" && collection.total > collection.page.pageSize && (
          <CollectionPagination collection={collection} className="px-4" />
        )}
      </div>

      <div>
        <Button type="button" variant="ghost" onClick={onReplaceFile}>
          Choose another file
        </Button>
      </div>
    </div>
  );
}

// ── SummaryBadge: a count + domain validation state (local composition) ─────
// Documented in product-local-composition-decisions.ts (coaching-import-summary
// -card): pairs a count with a @vjal/ui Card + Badge for the import preview
// summary. Replace with @vjal/ui Kpi if numeric status summaries recur elsewhere.
function SummaryBadge({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant: "success" | "warning" | "danger";
}) {
  return (
    <Card className="h-full shadow-none" data-testid={`bulk-summary-${label.toLowerCase()}`}>
      <p className="m-0 text-sm text-ink-muted">{label}</p>
      <p className="m-0 mt-1 text-2xl font-semibold text-strong" data-testid={`bulk-summary-${label.toLowerCase()}-value`}>
        {value}
      </p>
      <Badge variant={variant}>{label.toLowerCase()}</Badge>
    </Card>
  );
}

function ResultStep({
  result,
}: {
  result: BulkImportResultView;
}) {
  const resultRows = result.rowResults as ResultTableRow[];
  const collection = useBulkImportResultCollection(resultRows);
  const variant = result.succeeded && !result.partial ? "success" : result.partial ? "warning" : "error";
  return (
    <div className="flex flex-col gap-3" data-testid="bulk-import-result">
      <Alert variant={variant}>{result.summary}</Alert>
      {result.rowResults.length > 0 && (
        <div className="rounded-xl border border-line bg-white">
          <DataTableV2 collection={collection} caption="Bulk import outcome (from the server)" />
          {collection.uiState === "ready" && collection.total > collection.page.pageSize && (
            <CollectionPagination collection={collection} className="px-4" />
          )}
        </div>
      )}
    </div>
  );
}
